/**
 * Where focus goes when the element that had it disappears (a screen swap, a
 * dialog whose opener unmounted, a popover whose trigger re-rendered away).
 * Without this, focus falls to <body> and the next Tab restarts at the skip
 * link, several stops away from the board.
 */

/** The board's roving tab stop, when a board is on screen. */
const BOARD_CELL = '[role="gridcell"][tabindex="0"]';

/**
 * Focus the main content: the board's current cell during a game, otherwise
 * the `#main` landmark (made programmatically focusable on demand, so no
 * screen has to remember to add `tabIndex={-1}`).
 */
export function focusMain(): void {
  const cell = document.querySelector<HTMLElement>(BOARD_CELL);
  if (cell) {
    cell.focus({ preventScroll: true });
    return;
  }
  const main = document.getElementById('main');
  if (!main) return;
  if (!main.hasAttribute('tabindex')) main.tabIndex = -1;
  main.focus({ preventScroll: true });
}

/** True when nothing meaningful has focus (it was lost to <body>). */
export function focusIsLost(): boolean {
  const active = document.activeElement;
  return !active || active === document.body;
}

/**
 * Focus the first field marked `aria-invalid="true"` inside `root`, so a
 * failed submit lands the user on the problem and the screen reader reads the
 * field's error (wired up through `aria-describedby`). Returns false when no
 * field is invalid.
 */
export function focusFirstInvalid(root: HTMLElement | null): boolean {
  const field = root?.querySelector<HTMLElement>('[aria-invalid="true"]');
  if (!field) return false;
  field.focus();
  return true;
}
